import React, { useEffect, useRef, useState } from "react";
import { BrainCircuit, Target, Monitor, Cloud, Building2, Rocket } from "lucide-react";

const strengths = [
  {
    id: "agentic-ai",
    icon: BrainCircuit,
    title: "Agentic AI Expertise",
    description:
      "We specialize in autonomous AI systems that reason, plan, and execute — not just chatbots. Our agents handle complex, multi-step workflows without human intervention.",
    highlight: "Autonomous reasoning & execution",
  },
  {
    id: "product-thinking",
    icon: Target,
    title: "Product-First Thinking",
    description:
      "Every AI system we build starts with user experience and business outcomes. We think like product companies — not like IT vendors or software agencies.",
    highlight: "Outcome-driven product design",
  },
  {
    id: "full-stack",
    icon: Monitor,
    title: "Mobile + Web + AI Development",
    description:
      "We deliver complete solutions: AI backend, web platform, and native mobile apps. One team, one vision, one cohesive product across all touchpoints.",
    highlight: "End-to-end tech capability",
  },
  {
    id: "cloud",
    icon: Cloud,
    title: "Scalable Cloud Infrastructure",
    description:
      "Our systems are built to scale — from 100 to 10 million interactions. Cloud-native architecture with auto-scaling, zero downtime, and enterprise SLAs.",
    highlight: "99.9% uptime guarantee",
  },
  {
    id: "enterprise",
    icon: Building2,
    title: "Enterprise Architecture",
    description:
      "Security-first, compliance-ready, and built for enterprise demands. SOC 2 aligned, GDPR-aware, and designed to pass enterprise security reviews.",
    highlight: "Enterprise-grade security",
  },
  {
    id: "delivery",
    icon: Rocket,
    title: "End-to-End Product Delivery",
    description:
      "We take ownership from discovery to deployment and beyond. No handoffs, no gaps. We own the entire product lifecycle and ensure successful adoption.",
    highlight: "Full lifecycle ownership",
  },
];

const WhyUs = () => {
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      ref={sectionRef}
      className="py-24 md:py-32 bg-[#080808] relative overflow-hidden"
    >
      {/* Background grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-20"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(0,229,153,0.08) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="mb-16 text-center">
          <span className="text-xs font-bold text-[#00E599] uppercase tracking-widest block mb-4">
            Our Edge
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-5"
            style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.025em" }}
          >
            Why CodeIntel AI Labs
          </h2>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            We're not a development shop. We're a product company that happens to build for other
            businesses — bringing the same rigor we'd apply to our own products.
          </p>
        </div>

        {/* Strengths Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {strengths.map((strength, index) => {
            const Icon = strength.icon;
            return (
              <div
                key={strength.id}
                data-testid={`why-us-card-${strength.id}`}
                className={`why-card rounded-2xl p-8 transition-all duration-700 ${
                  inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${(index % 3) * 100}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-[#00E599]/8 border border-[#00E599]/15 flex items-center justify-center mb-6">
                  <Icon size={22} className="text-[#00E599]" />
                </div>
                <h3
                  className="text-lg font-bold text-white mb-3"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {strength.title}
                </h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed mb-5">
                  {strength.description}
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00E599]/6 border border-[#00E599]/12">
                  <div className="w-1 h-1 rounded-full bg-[#00E599]" />
                  <span className="text-xs text-[#00E599] font-medium">{strength.highlight}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyUs;
